import {Box, Typography} from '@mui/material';
import {Page, PageSeo, PageType} from '../../components/common/container';
import {Prose, SectionTitle, Title} from '../../components/common/text';
import {Trans, useTranslation} from 'next-i18next';
import {GetStaticProps} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import Link from 'next/link';
import {CONTACT} from '../../constants';
import {Warning} from '../../components/common/frame';
import {LegalNotice} from './mention';

export const getStaticProps: GetStaticProps<{}> = async (context) => {
    const translation = await serverSideTranslations(context.locale, [
        'common',
        'cgu',
        'legal-notice',
    ]);
    return {
        props: translation,
        revalidate: 86400, // 1 jour
    };
};

export default function Cgu() {
    const {t} = useTranslation('cgu');

    return (
        <Page>
            <PageSeo page={PageType.CGU} />
            <LegalNotice />
            <Title>{t('title')}</Title>
            <Box
                sx={{
                    maxWidth: 550,
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    paddingLeft: 4,
                    paddingRight: 4,
                }}
            >
                <Updated />
                <CguFirstArticle />
                <CguSecondArticle />
                <CguThirdArticle />
                <CguFourthArticle />
                <CguFifthArticle />
                <CguSixthArticle />
                <CguSeventhArticle />
                <CguEightArticle />
                <CguNinthArticle />
            </Box>
        </Page>
    );
}

function Updated() {
    const {t} = useTranslation('cgu');

    return (
        <Typography variant="caption">
            {t('updated', {date: new Date(Date.UTC(2023, 6, 30, 0, 0, 0))})}
        </Typography>
    );
}

function CguFirstArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>1. {t('article.1.title')}</SectionTitle>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.1.content"
                    components={{kuneKuneLink: <Link href={'/'} />}}
                />
            </Prose>
        </>
    );
}

function CguSecondArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>2. {t('article.2.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.2.content.1" />
            </Prose>
            <Prose>{t('article.2.content.2')}</Prose>
            <Prose>{t('article.2.content.3')}</Prose>
            <Warning>
                <Prose>
                    <Trans
                        t={t}
                        i18nKey="article.2.content.langWarning"
                        components={{linkToFrCgu: <Link href="/fr/info/cgu" />}}
                    />
                </Prose>
            </Warning>
        </>
    );
}

function CguThirdArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>3. {t('article.3.title')}</SectionTitle>
            <Prose>{t('article.3.content.1')}</Prose>
            <Prose>{t('article.3.content.2')}</Prose>
            <Prose>{t('article.3.content.3')}</Prose>
            <Prose>{t('article.3.content.4')}</Prose>
        </>
    );
}

function CguFourthArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>4. {t('article.4.title')}</SectionTitle>
            <Prose>{t('article.4.content.1')}</Prose>
            <Prose>{t('article.4.content.2')}</Prose>
            <ul>
                <li>{t('article.4.content.2-list.1')}</li>
                <li>{t('article.4.content.2-list.2')}</li>
                <li>{t('article.4.content.2-list.3')}</li>
                <li>{t('article.4.content.2-list.4')}</li>
                <li>{t('article.4.content.2-list.5')}</li>
                <li>{t('article.4.content.2-list.6')}</li>
            </ul>
        </>
    );
}

function CguFifthArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>5. {t('article.5.title')}</SectionTitle>
            <Prose>{t('article.5.content.1')}</Prose>
            <Prose>{t('article.5.content.2')}</Prose>
            <Prose>{t('article.5.content.3')}</Prose>
            <Prose>{t('article.5.content.4')}</Prose>
        </>
    );
}

function CguSixthArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>6. {t('article.6.title')}</SectionTitle>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.6.content"
                    components={{
                        privacyPolicyLink: <Link href={'/info/privacy-policy'} />,
                    }}
                />
            </Prose>
        </>
    );
}

function CguSeventhArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>7. {t('article.7.title')}</SectionTitle>
            <Prose>{t('article.7.content.1')}</Prose>
            <Prose>{t('article.7.content.2', {contactEmail: CONTACT.EMAIL})}</Prose>
            <Prose>{t('article.7.content.3')}</Prose>
            <Prose>{t('article.7.content.4')}</Prose>
        </>
    );
}

function CguEightArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>8. {t('article.8.title')}</SectionTitle>
            <Prose>{t('article.8.content.1')}</Prose>
            <Prose>{t('article.8.content.2')}</Prose>
            <Prose>{t('article.8.content.3')}</Prose>
        </>
    );
}
function CguNinthArticle() {
    const {t} = useTranslation('cgu');

    return (
        <>
            <SectionTitle>9. {t('article.9.title')}</SectionTitle>
            <Prose>{t('article.9.content', {contactEmail: CONTACT.EMAIL})}</Prose>
        </>
    );
}
